import React, { useState, useEffect } from 'react';
import { useLexicon } from '../../../hooks/useLexicon';
import styled from 'styled-components';
import { visibleScrollbar } from '../../../styledMixins';
import ScrollableArea from '../../Shared/ScrollableArea';
import { useHistory } from 'react-router-dom';
import Eye from '../../../static/icons/Eye.svg';
import MarkEdit from '../../../static/img/markEdit.svg';
import EditHover from '../../../static/img/EditHover.svg';
import plusIcon from '../../../static/img/Plus-violet.svg';
import deleteIcon from '../../../static/icons/delete-logo.svg';
import { isTabletView } from '../../ReactiveRender';
import MenuButton from '../../MenuButton';
import ImprovedSecondarySideBar from '../../ImprovedSecondarySideBar';
import ItemSettingsModal from '../../Shared/ItemSettingsModal';
import SettingsTagsDisplay from '../../Shared/SettingsTagsDisplay';
import StepLabelBadge from '../../Shared/StepLabelBadge';
import SetupStepSection from '../../Shared/SetupStepSection';
import { useAvailableYears, useSubjects, useSubjectsForYearFromClasses, useSubjectsForYearsFromClasses, useUserStateSubjects, useTaskTypes, useResponseTypes, TASK_TYPE_LABELS, RESPONSE_TYPE_LABELS } from '../../../hooks/useSubjects';
import { useUserStates, useMarkingToolStates } from '../../../hooks/useUserStates';
import { withSelectedSubjectsPreserved } from '../../../utils/subjects';
import { TextInput, HeadingAndFilterContainer } from '../../CreateNewMarkingCriteria/CreateNewMarkingCriteriaDesktop/style';
import ShortAnswerDialog from '../../Shared/Dialogs/shortAnswer';

function CreateNewShortAnswerDesktop(props) {
  const { t } = useLexicon();
  const {
    shortAnswer,
    updateShortAnswer,
    handleTitleChange,
    handleMarksChange,
    handleCriteriaChange,
    handleDeleteCriteria,
    handleAddCriteria,
    handleHighestMarksChange,
    handleMarksFromChange,
    rangesEnabled = false,
    toggleRangesEnabled = () => {},
    expandedRangeRows = new Set(),
    toggleRowRange = () => {},
    removeRowRange = () => {},
    saveShortAnswer,
    isUpdating,
    shortAnswerId,
  } = props;

  const history = useHistory();
  const { states: userStates } = useUserStates();
  const state = userStates;
  const [localYears, setLocalYears] = useState(null);
  const [localSubjects, setLocalSubjects] = useState(null);
  const [localTaskTypes, setLocalTaskTypes] = useState(null);
  const [localResponseTypes, setLocalResponseTypes] = useState(null);

  const selectedYears = localYears !== null ? localYears : (shortAnswer?.years || []);
  const rawItemSubjects = shortAnswer?.subjects || [];
  const normalizedItemSubjects = rawItemSubjects.map(s => (typeof s === 'object' && s !== null) ? (s.subjectCode || s.code || s.value || s) : s).filter(Boolean);
  const selectedSubjects = localSubjects !== null ? localSubjects : normalizedItemSubjects;
  const selectedTaskTypes = localTaskTypes !== null ? localTaskTypes : (shortAnswer?.taskTypes || (shortAnswer?.taskType ? [shortAnswer.taskType] : []));
  const selectedResponseTypes = localResponseTypes !== null ? localResponseTypes : (shortAnswer?.responseTypes || (shortAnswer?.responseType ? [shortAnswer.responseType] : []));

  const { years: availableYears } = useAvailableYears();
  const yearForFilter = selectedYears?.find((y) => availableYears?.includes(y)) ?? selectedYears?.[0];
  const yearsForFilter = Array.isArray(selectedYears) && selectedYears.length ? selectedYears : (yearForFilter != null ? [yearForFilter] : []);
  const scopeStates = useMarkingToolStates();
  const { subjects: subjectsByYear } = useSubjects(state, yearForFilter);
  const { subjects: subjectsFromClasses } = useSubjectsForYearFromClasses(yearForFilter, scopeStates);
  const { subjects: subjectsForSelectedYears } = useSubjectsForYearsFromClasses(selectedYears, scopeStates);
  const { subjects: allClassSubjects } = useUserStateSubjects(scopeStates);
  const subjectsForYear = (subjectsByYear?.length ? subjectsByYear : subjectsFromClasses) || [];
  const subjectsSource = selectedYears?.length
    ? (subjectsForSelectedYears?.length ? subjectsForSelectedYears : allClassSubjects)
    : [];
  const subjectsEnriched = (subjectsSource.length ? subjectsSource : subjectsForYear).map((s) => {
    const fromClass = (subjectsForSelectedYears?.length ? subjectsForSelectedYears : subjectsFromClasses)?.find((c) => c.code === s.code);
    return fromClass ? { ...s, taskTypes: fromClass.taskTypes || [], responseTypes: fromClass.responseTypes || [] } : s;
  });
  const { taskTypes: filteredTaskTypes } = useTaskTypes(selectedSubjects, state, yearsForFilter.length ? yearsForFilter : yearForFilter);
  const { responseTypes: filteredResponseTypes } = useResponseTypes(selectedSubjects, selectedTaskTypes, state, yearsForFilter.length ? yearsForFilter : yearForFilter);

  // Keep an edited tool's existing (possibly out-of-state) subject in the
  // options so state scoping can't silently strip it on save.
  const availableSubjects = withSelectedSubjectsPreserved(
    selectedYears?.length ? subjectsEnriched : [],
    selectedSubjects,
    rawItemSubjects,
  );
  const availableTaskTypes = selectedSubjects.length ? filteredTaskTypes : [];
  const availableResponseTypes = (selectedSubjects.length && selectedTaskTypes.length) ? filteredResponseTypes : [];

  // Auto-remove selected task types that are no longer valid
  useEffect(() => {
    if (!selectedTaskTypes.length) return;
    if (!availableTaskTypes.length) { setLocalTaskTypes([]); return; }
    const validValues = new Set(availableTaskTypes.map(t => t.value ?? t));
    const filtered = selectedTaskTypes.filter(t => validValues.has(t));
    if (filtered.length !== selectedTaskTypes.length) setLocalTaskTypes(filtered);
  }, [availableTaskTypes]);

  // Auto-remove selected response types that are no longer valid
  useEffect(() => {
    if (!selectedResponseTypes.length) return;
    if (!availableResponseTypes.length) { setLocalResponseTypes([]); return; }
    const validValues = new Set(availableResponseTypes.map(r => r.value ?? r));
    const filtered = selectedResponseTypes.filter(r => validValues.has(r));
    if (filtered.length !== selectedResponseTypes.length) setLocalResponseTypes(filtered);
  }, [availableResponseTypes]);

  const onYearsChange = (v) => {
    setLocalYears(v);
    if (!v?.length) {
      setLocalSubjects([]);
      setLocalTaskTypes([]);
      setLocalResponseTypes([]);
    }
  };
  const onSubjectsChange = (v) => {
    setLocalSubjects(v);
  };
  const onTaskTypesChange = (v) => {
    setLocalTaskTypes(v);
  };

  const [isEditing, setIsEditing] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [isShowMenu, setShowMenu] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const tabletView = isTabletView();

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      setIsEditing(false);
    }
  };

  const handleOnBlur = () => {
    setIsEditing(false);
  };

  const handleYearToggle = (year) => {
    setLocalYears(selectedYears.includes(year) ? selectedYears.filter((y) => y !== year) : [...selectedYears, year]);
  };

  const handleSubjectToggle = (subject) => {
    setLocalSubjects(selectedSubjects.includes(subject) ? selectedSubjects.filter((s) => s !== subject) : [...selectedSubjects, subject]);
  };

  const handleTaskTypeToggle = (type) => {
    setLocalTaskTypes(selectedTaskTypes.includes(type) ? selectedTaskTypes.filter((t) => t !== type) : [...selectedTaskTypes, type]);
  };

  const handleResponseTypeToggle = (type) => {
    setLocalResponseTypes(selectedResponseTypes.includes(type) ? selectedResponseTypes.filter((r) => r !== type) : [...selectedResponseTypes, type]);
  };

  const handleGoBack = () => {
    history.push('/tools/shortanswers');
  };

  const canSave = selectedYears.length > 0 && selectedSubjects.length > 0;

  const handleSave = () => {
    if (!canSave) return;
    saveShortAnswer({
      years: selectedYears,
      subjects: selectedSubjects,
      taskTypes: selectedTaskTypes,
      responseTypes: selectedResponseTypes,
    });
  };

  const handleSaveSettings = async (settings) => {
    try {
      await updateShortAnswer({
        years: settings.years,
        subjects: settings.subjects,
        taskTypes: settings.taskTypes,
        responseTypes: settings.responseTypes,
      });
      setLocalYears(settings.years || []);
      setLocalSubjects(settings.subjects || []);
      setLocalTaskTypes(settings.taskTypes || []);
      setLocalResponseTypes(settings.responseTypes || []);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  return (
    <>
      <MainContainer>
        <ImprovedSecondarySideBar
          isShowMenu={isShowMenu}
          setShowMenu={setShowMenu}
          id={shortAnswerId}
        />
        <ContentWrapper>
          <InnerContainer>
            {tabletView && (
              <HeadingAndFilterContainer>
                <MenuButton setShowMenu={setShowMenu} />
              </HeadingAndFilterContainer>
            )}
            <RightContainer>
              <HeadingContainer>
                <HeadingLeft>
                  {isUpdating && (
                    <BackArrow onClick={handleGoBack}>&lt;</BackArrow>
                  )}
                  {isEditing ? (
                    <Heading id="shortAnswerTitleContainer">
                      <TextInput
                        placeholder="Name of short answer"
                        id="shortAnswerName"
                        value={shortAnswer.title}
                        onChange={handleTitleChange}
                        onBlur={() => handleOnBlur()}
                        onKeyPress={handleKeyPress}
                        maxLength="100"
                      />
                    </Heading>
                  ) : (
                    <Heading onClick={() => setIsEditing(true)}>
                      {shortAnswer.title || 'Title'}
                      <EditIcon src={MarkEdit} />
                      <EditIconHover src={EditHover} />
                    </Heading>
                  )}
                </HeadingLeft>
                {!isUpdating && (
                  <HeaderRight>
                    <PreviewButton onClick={() => setOpenPreviewDialog(true)}>
                      <PreviewButtonIcon src={Eye} />
                      <PreviewButtonText>Preview</PreviewButtonText>
                    </PreviewButton>
                    <SaveButton onClick={handleSave} $disabled={!canSave}>
                      <SaveButtonText>Save and Publish</SaveButtonText>
                    </SaveButton>
                  </HeaderRight>
                )}
              </HeadingContainer>
              {isUpdating && (
                <SettingsTagsRow>
                  <SettingsTagsDisplay
                    years={shortAnswer?.years || []}
                    subjects={shortAnswer?.metadata?.subjects || []}
                    taskTypes={shortAnswer?.taskTypes || []}
                    responseTypes={shortAnswer?.responseTypes || []}
                    taskTypeLabels={Object.fromEntries((availableTaskTypes ?? []).map(t => [(t && typeof t === 'object' ? t.value : t), (t && typeof t === 'object' ? t.label ?? t.title ?? t.value : t)]))}
                    responseTypeLabels={Object.fromEntries((availableResponseTypes ?? []).map(r => [(r && typeof r === 'object' ? r.value : r), (r && typeof r === 'object' ? r.label ?? r.title ?? r.value : r)]))}
                    onSettingsClick={() => setIsSettingsOpen(!isSettingsOpen)}
                    showSettingsButton={true}
                  />
                </SettingsTagsRow>
              )}

              <MainContentSection>
              {/* Step 1 - Set Up Section (ONLY for create mode) */}
              {!isUpdating && (
                <SetupStepSection
                  availableYears={availableYears}
                  availableSubjects={availableSubjects}
                  availableTaskTypes={availableTaskTypes}
                  availableResponseTypes={availableResponseTypes}
                  selectedYears={selectedYears}
                  selectedSubjects={selectedSubjects}
                  selectedTaskTypes={selectedTaskTypes}
                  selectedResponseTypes={selectedResponseTypes}
                  onYearsChange={onYearsChange}
                  onSubjectsChange={onSubjectsChange}
                  onTaskTypesChange={onTaskTypesChange}
                  onResponseTypesChange={setLocalResponseTypes}
                  isUpdating={isUpdating}
                />
              )}

              {/* Step 2 - Add Criteria Section (ONLY for create mode) */}
              {!isUpdating && (
                <StepLabelRow>
                  <StepLabelBadge>Step 2 - Add Criteria</StepLabelBadge>
                </StepLabelRow>
              )}

              {/* Criteria Content */}
              <CriteriaContainer>
                <HighestMarksRow>
                  <HighestMarksContainer>
                    <HighestMarksLabel>{t('Highest Marks')}</HighestMarksLabel>
                    <HighestMarksInput
                      type="number"
                      min="0"
                      value={shortAnswer.highestMarks ?? ''}
                      onChange={(e) => handleHighestMarksChange(e.target.value)}
                    />
                  </HighestMarksContainer>
                  <ToggleContainer>
                    <ToggleSwitch
                      type="button"
                      $active={rangesEnabled}
                      onClick={toggleRangesEnabled}
                    >
                      <ToggleKnob $active={rangesEnabled} />
                    </ToggleSwitch>
                    <ToggleLabel>{t('Use mark ranges')}</ToggleLabel>
                  </ToggleContainer>
                </HighestMarksRow>

                {rangesEnabled && (
                  <RangesHint>
                    Enter ranges from low to high, e.g. 0–1, then 2–4.
                  </RangesHint>
                )}

                <CriteriaHeader>
                  <MarksHeaderLabel>{t('Marks')}</MarksHeaderLabel>
                  <CriteriaHeaderLabel>Criteria</CriteriaHeaderLabel>
                </CriteriaHeader>

                <CriteriaList>
                  {shortAnswer.criterias.map((criteriaItem, index) => {
                    const hasMarksFromValue =
                      criteriaItem.marksFrom !== undefined &&
                      criteriaItem.marksFrom !== null;
                    const isRangeExpanded =
                      rangesEnabled && expandedRangeRows.has(index);
                    const showRangeInput = isRangeExpanded || hasMarksFromValue;
                    const marksFromValue = criteriaItem.marksFrom ?? '';

                    return (
                      <CriteriaItem key={index}>
                        {showRangeInput && (
                          <>
                            <MarksInput
                              type="number"
                              min="0"
                              value={marksFromValue}
                              placeholder="0"
                              title="Range start"
                              onChange={(e) =>
                                handleMarksFromChange &&
                                handleMarksFromChange(index, e.target.value)
                              }
                            />
                            <MarksSeparator>–</MarksSeparator>
                          </>
                        )}
                        <MarksInput
                          type="number"
                          min="0"
                          value={criteriaItem.marks}
                          onChange={(e) => handleMarksChange(index, e.target.value)}
                        />
                        {rangesEnabled && !showRangeInput && (
                          <RangeExpandButton
                            type="button"
                            title={t('Add mark range')}
                            onClick={() => toggleRowRange(index)}
                          >
                            +
                          </RangeExpandButton>
                        )}
                        {showRangeInput && (
                          <RangeRemoveButton
                            type="button"
                            title={t('Remove mark range')}
                            onClick={() => removeRowRange(index)}
                          >
                            ×
                          </RangeRemoveButton>
                        )}
                        <CriteriaInput
                          type="text"
                          placeholder="Enter criteria..."
                          value={criteriaItem.criteria}
                          onChange={(e) => handleCriteriaChange(index, e.target.value)}
                        />
                        <DeleteButton onClick={() => handleDeleteCriteria(index)}>
                          <img src={deleteIcon} alt="Delete" />
                        </DeleteButton>
                      </CriteriaItem>
                    );
                  })}
                </CriteriaList>

                <AddCriteriaButton onClick={handleAddCriteria}>
                  <img src={plusIcon} alt="Add" />
                  Add Criteria
                </AddCriteriaButton>
              </CriteriaContainer>

              {/* Bottom Buttons */}
              <BottomButtonsContainer>
                <PreviewButton onClick={() => setOpenPreviewDialog(true)}>
                  <PreviewButtonIcon src={Eye} />
                  <PreviewButtonText>Preview</PreviewButtonText>
                </PreviewButton>
                <SaveButton onClick={handleSave} $disabled={!canSave}>
                  <SaveButtonText>Save and Publish</SaveButtonText>
                </SaveButton>
              </BottomButtonsContainer>
              </MainContentSection>
            </RightContainer>
          </InnerContainer>

          {/* Settings Modal */}
          <ItemSettingsModal
            isOpen={isSettingsOpen}
            onClose={() => {
              setIsSettingsOpen(false);
              setLocalYears(null);
              setLocalSubjects(null);
              setLocalTaskTypes(null);
              setLocalResponseTypes(null);
            }}
            onSave={handleSaveSettings}
            availableYears={availableYears}
            availableSubjects={availableSubjects}
            taskTypes={availableTaskTypes}
            responseTypes={availableResponseTypes}
            selectedYears={selectedYears}
            setSelectedYears={onYearsChange}
            selectedSubjects={selectedSubjects}
            setSelectedSubjects={onSubjectsChange}
            selectedTaskTypes={selectedTaskTypes}
            setSelectedTaskTypes={onTaskTypesChange}
            selectedResponseTypes={selectedResponseTypes}
            setSelectedResponseTypes={setLocalResponseTypes}
          />
        </ContentWrapper>
      </MainContainer>
      {openPreviewDialog && (
        <ShortAnswerDialog
          shortAnswer={shortAnswer}
          setShortAnswerPreviewDialog={setOpenPreviewDialog}
        />
      )}
    </>
  );
}

// Styled Components
const MainContainer = styled.div`
  width: 100%;
  position: relative;
  align-items: flex-start;
  background-color: var(--white);
  display: flex;
  flex-direction: row;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100vh;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  position: relative;
`;

const RightContainer = styled(ScrollableArea).attrs({ autoHide: 'leave' })`
  width: 100%;
  background: #ffffff;
  height: 100vh;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #c9c6cc80;
  background: #ffffff;
`;

const HeadingLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  margin-right: 8px;
`;

const BackArrow = styled.span`
  font-family: IBM Plex Sans;
  font-size: 20px;
  font-weight: 400;
  color: #7200e0;
  cursor: pointer;
  padding: 4px 8px;

  &:hover {
    opacity: 0.9;
  }
`;

const Heading = styled.p`
  font-family: IBM Plex Sans;
  font-size: 16px;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: -0.01em;
  text-align: left;
  display: flex;
  align-items: center;
  padding: 8px 6px;
  gap: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  margin: 0;
  flex: 1;
  min-width: 0;

  &:hover {
    border: 1px solid #c9c6cc80;
  }
`;

const EditIcon = styled.img`
  width: 20px;
  height: 20px;
  opacity: 0.85;
  flex-shrink: 0;
`;

const EditIconHover = styled.img`
  width: 20px;
  height: 20px;
  display: none;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SettingsTagsRow = styled.div`
  padding: 12px 24px 16px;
  border-bottom: 1px solid #c9c6cc80;
  background: #FBF7FE;
  border-radius: 0;
`;

const MainContentSection = styled.div`
  padding: 0 24px;
`;

const PreviewButton = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 32px;
  border: 1px solid #7200e0;
  gap: 4px;
  background: #ffffff;
  cursor: pointer;
  height: 40px;

  &:hover {
    background-color: #f9f5ff;
  }
`;

const PreviewButtonIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const PreviewButtonText = styled.p`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  color: #7200e0;
  margin: 0;
`;

const SaveButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 8px 16px;
  border-radius: 32px;
  background: #7200e0;
  cursor: pointer;

  &:hover {
    background-color: rgba(80, 0, 157, 1);
  }

  ${({ $disabled }) =>
    $disabled &&
    `
    background: #c9c6cc;
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const SaveButtonText = styled.p`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  color: #ffffff;
  margin: 0;
`;

// Step Section Styles
const StepLabelRow = styled.div`
  padding: 16px 0;
  background: #ffffff;
`;

// Criteria Section Styles
const CriteriaContainer = styled.div`
  padding: 0 0 20px;
  background: #ffffff;
`;

const HighestMarksContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const HighestMarksLabel = styled.span`
  font-family: IBM Plex Sans;
  font-size: 16px;
  font-weight: 500;
  color: #1e252a;
`;

const HighestMarksInput = styled.input`
  width: 60px;
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #C9C6CC;
  border-radius: 6px;
  font-family: IBM Plex Sans;
  font-size: 16px;
  text-align: center;
  outline: none;
  background-color: #ffffff;

  &:focus {
    border-color: #7200E0;
    box-shadow: 0 0 0 2px rgba(114, 0, 224, 0.1);
  }
`;

const CriteriaHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  padding: 0 8px;
`;

const MarksHeaderLabel = styled.span`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 600;
  color: #4B464F;
  width: 60px;
`;

const CriteriaHeaderLabel = styled.span`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 600;
  color: #4B464F;
  flex: 1;
`;

const HighestMarksRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleSwitch = styled.button`
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: none;
  cursor: pointer;
  background-color: ${(props) => (props.$active ? '#7200E0' : '#C9C6CC')};
  transition: background-color 0.2s;
  padding: 0;
  flex-shrink: 0;
`;

const ToggleKnob = styled.div`
  position: absolute;
  top: 2px;
  left: ${(props) => (props.$active ? '20px' : '2px')};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #fff;
  transition: left 0.2s;
`;

const ToggleLabel = styled.span`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  color: #4B464F;
`;

const RangesHint = styled.div`
  font-family: IBM Plex Sans;
  font-size: 13px;
  color: #7B7382;
  margin: -8px 0 16px;
`;

const MarksSeparator = styled.span`
  font-family: IBM Plex Sans;
  font-size: 16px;
  color: #4B464F;
  flex-shrink: 0;
`;

const RangeExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px dashed #7200E0;
  background-color: transparent;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 16px;
  color: #7200E0;
  transition: all 0.2s;
  padding: 0;

  &:hover {
    background-color: rgba(114, 0, 224, 0.08);
  }
`;

const RangeRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #C9C6CC;
  background-color: transparent;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 14px;
  color: #7b7382;
  transition: all 0.2s;
  padding: 0;

  &:hover {
    background-color: #FEE2E2;
    border-color: #DC2626;
    color: #DC2626;
  }
`;

const CriteriaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const CriteriaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px;
  border-radius: 6px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F8F7F9;
  }
`;

const MarksInput = styled.input`
  width: 60px;
  height: 44px;
  padding: 8px 12px;
  border: 1px solid #C9C6CC;
  border-radius: 6px;
  font-family: IBM Plex Sans;
  font-size: 16px;
  text-align: center;
  outline: none;
  flex-shrink: 0;

  &:focus {
    border-color: #7200E0;
    box-shadow: 0 0 0 2px rgba(114, 0, 224, 0.1);
  }
`;

const CriteriaInput = styled.input`
  flex: 1;
  height: 44px;
  padding: 8px 16px;
  border: 1px solid #C9C6CC;
  border-radius: 6px;
  font-family: IBM Plex Sans;
  font-size: 16px;
  color: #1e252a;
  outline: none;

  &:focus {
    border-color: #7200E0;
    box-shadow: 0 0 0 2px rgba(114, 0, 224, 0.1);
  }

  &::placeholder {
    color: #9E9E9E;
  }
`;

const DeleteButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: #FEE2E2;
    opacity: 1;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const AddCriteriaButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #7200E0;
  border-radius: 20px;
  background-color: transparent;
  cursor: pointer;
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  color: #7200E0;
  transition: all 0.2s;

  &:hover {
    background-color: #F5EDFC;
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

const BottomButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 20px 0;
  background: #ffffff;
  border-top: 1px solid #e5e5e5;
`;

export default CreateNewShortAnswerDesktop;
